import { Camera, FileText, Paperclip, Trash2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Banner } from '@/components/feedback/Banner'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Select } from '@/components/ui/Select'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'
import {
  DEFAULT_FILE_KIND,
  FILE_KINDS,
  asFileKind,
  fileKindLabelKey,
  type FileKind,
} from '@/lib/fileKinds'
import { useIsOnline } from '@/lib/useConnection'
import { useFormat } from '@/i18n/useFormat'
import {
  useCaseFiles,
  useDeleteCaseFile,
  useDownloadCaseFile,
} from '@/features/cases/api/useCaseFiles'
import { useUploadQueue } from '@/features/cases/api/useUploadQueue'
import type { CaseFile } from '@/types/domain'

/* The file area (Story 8.1, FR-45, UX-DR-14).
 *
 * Four affordances the AC names: drag-and-drop, camera capture on mobile, files
 * grouped by their case, and type and uploader shown on every row.
 *
 * "Grouped by case" needs no grouping control here, and that is worth saying
 * because it looks like an omission: this component is mounted ON a case and
 * queries that case's endpoint, so the grouping is structural rather than a
 * filter somebody can change. A file list spanning cases would be a different
 * surface with a different scope question.
 *
 * WITHIN the case the list IS grouped, by what kind of document each file is
 * (0045). That is a second sense of "type" from the one the AC meant — the AC's
 * is the format, PDF or Photo, and it is still on every row — and the two are
 * not interchangeable: a case with forty PDFs is grouped by format into one
 * heap of forty, while the question anybody actually arrives with is "where is
 * the customs declaration".
 */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** The FORMAT, in words, from the content type. The AC asks the list to show
 *  the type — "PDF" is what a user reads, not "application/pdf".
 *
 *  Kept beside the document kind rather than replaced by it. They answer
 *  different questions: the kind says what the paper IS, this says what will
 *  happen when you press it. */
function fileFormat(contentType: string): string {
  if (!contentType) return 'File'
  if (contentType.startsWith('image/')) return 'Photo'
  const subtype = contentType.split('/')[1] ?? ''
  if (subtype.includes('pdf')) return 'PDF'
  if (subtype.includes('sheet') || subtype.includes('excel')) return 'Sheet'
  if (subtype.includes('word') || subtype.includes('document')) return 'Doc'
  return subtype ? subtype.slice(0, 12).toUpperCase() : 'File'
}

export function CaseFiles({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const format = useFormat()

  const { data: files = [], isLoading } = useCaseFiles(caseId)
  const download = useDownloadCaseFile()
  const remove = useDeleteCaseFile(caseId)

  /* Every upload goes through the durable queue (Story 8.2, UX-DR-19).
   *
   * There is no longer a direct-send path beside it, deliberately. Two ways to
   * upload would mean two ways to fail, and the one a user reached by dropping
   * a file rather than by pressing a button would be the one with no queue
   * behind it — an inconsistency nobody could predict from the interface. */
  const queue = useUploadQueue(caseId)
  const isOnline = useIsOnline()

  const [dragging, setDragging] = useState(false)
  const pickerRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)

  /* Files chosen but not yet queued, waiting for their kind to be declared.
   *
   * A STAGING STEP between picking and queueing, and it is the only place in
   * this component where a file exists and is not being sent. It has to be:
   * the kind is persisted WITH the entry (see uploadQueue), because a send can
   * happen days later on a different network, so it must be known before the
   * write rather than attached to it afterwards.
   *
   * `null` means nothing is staged. An empty array would be a sheet open over
   * no files, which is a state with no way out that makes sense. */
  const [staged, setStaged] = useState<File[] | null>(null)
  const [stagedKind, setStagedKind] = useState<FileKind>(DEFAULT_FILE_KIND)

  /* Which row is armed for deletion. One id, not a set: two rows waiting for a
     second press is a screen where the next click destroys something the user
     has stopped looking at. */
  const [armedId, setArmedId] = useState<string | null>(null)

  /* Picking stages; the sheet sends. The two are separate so that CANCELLING
   * the sheet drops the files entirely — nothing has been written, nothing has
   * been sent, and a user who picked the wrong folder is not left deleting
   * eight rows they never meant to create. */
  const stage = (selected: FileList | null) => {
    if (!selected || selected.length === 0) return
    const picked = Array.from(selected)
    setStaged(picked)
    /* A photo from the camera is a photo. Guessing further — CMR from a
       filename, say — would be wrong often enough to be worse than 'other',
       because a guess is believed and a blank is checked; but every file
       arriving from an image capture genuinely is the thing 'photo' names, and
       making Operations pick it on every reload is how a picker becomes the
       reason people stop uploading. */
    setStagedKind(
      picked.every((file) => file.type.startsWith('image/'))
        ? 'photo'
        : DEFAULT_FILE_KIND,
    )
  }

  /* Files are persisted before anything is sent, so closing the tab mid-upload
   * loses nothing. The queue drains serially by itself: Operations select eight
   * reloading photos on a connection that barely holds one request, and eight
   * concurrent PUTs on that link is how all eight fail. */
  const send = () => {
    if (!staged) return
    void queue.add(staged, stagedKind)
    setStaged(null)
  }

  /* Queued files are rendered as rows ALONGSIDE the committed ones, not as a
   * separate panel below them. The last AC is explicit that pending state must
   * be visible "on the case rather than only inside the uploader" — a user
   * scanning the file list for their photo must find it there, marked pending,
   * rather than concluding it was lost because the list looks complete. */
  const pendingRows: CaseFile[] = queue.pending.map((entry) => ({
    id: `queued:${entry.clientKey}`,
    name: entry.fileName,
    sizeBytes: entry.sizeBytes,
    kind: entry.contentType,
    // The kind travels with the entry, so a queued file sits in the group it
    // will land in rather than jumping between groups when it commits.
    docKind: asFileKind(entry.docKind),
    // The uploader is the person looking at the screen; there is no server row
    // to name yet. The time is when it was QUEUED, which is what the user did.
    uploadedByName: t('cases.files.you'),
    uploadedAt: new Date(entry.queuedAt).toISOString(),
    pending: true,
  }))

  const rows = [...pendingRows, ...files]

  /* Grouped in the vocabulary's own order, not alphabetically and not by count.
     FILE_KINDS is ordered as somebody looks for paper — transport document,
     then the commercial set, then the contract, then photos, then whatever was
     never classified — and an order that changed with the contents would move
     a section every time a file was added to it. Empty groups are dropped. */
  const groups = FILE_KINDS.map((kind) => ({
    kind,
    files: rows.filter((file) => file.docKind === kind),
  })).filter((group) => group.files.length > 0)

  return (
    <div className="flex flex-col gap-3.5">
      {/* The queue banner, and it is deliberately NOT an error banner.
          Pending uploads are the feature working, not a failure — telling a
          user in red that eight photos "did not send" when they are queued and
          will send is what teaches people to press retry on a bad link and
          make everything worse. Neutral tone, a count, and a way to look. */}
      {queue.pendingCount > 0 ? (
        <Banner
          tone="info"
          title={t('cases.files.queuedTitle', { count: queue.pendingCount })}
          action={
            <Button variant="secondary" size="sm" onClick={queue.retryNow}>
              {t('cases.files.sendNow')}
            </Button>
          }
        >
          {isOnline
            ? t('cases.files.queuedSending')
            : t('cases.files.queuedOffline')}
        </Banner>
      ) : null}

      {remove.isError ? (
        <Banner tone="attention" title={t('cases.files.deleteFailed')}>
          {t('cases.files.deleteFailedHint')}
        </Banner>
      ) : null}

      <Card>
        <CardHeader
          action={
            <div className="flex items-center gap-2">
              {/* Camera capture, phone only. `capture` opens the camera
                  directly rather than the gallery, which is what an
                  Operations user at a warehouse actually wants. Hidden above
                  the phone breakpoint because a desktop has no camera roll
                  and the button would open a file picker labelled "Photo". */}
              <Button
                variant="secondary"
                size="sm"
                iconLeft={Camera}
                className="sm:hidden"
                onClick={() => cameraRef.current?.click()}
              >
                {t('cases.files.takePhoto')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={Upload}
                onClick={() => pickerRef.current?.click()}
              >
                {t('cases.files.upload')}
              </Button>
            </div>
          }
        >
          <CardTitle>{t('cases.files.title')}</CardTitle>
        </CardHeader>

        {/* Both inputs are visually hidden rather than `display:none`, so they
            remain reachable by assistive technology and by the label
            association the buttons above stand in for. */}
        <input
          ref={pickerRef}
          type="file"
          multiple
          className="sr-only"
          aria-label={t('cases.files.upload')}
          onChange={(event) => {
            stage(event.target.files)
            event.target.value = ''
          }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-label={t('cases.files.takePhoto')}
          onChange={(event) => {
            stage(event.target.files)
            event.target.value = ''
          }}
        />

        <div className="px-[18px] pb-4">
          {/* The drop target. `onDragOver` must preventDefault or the browser
              navigates to the file instead of handing it over — the single
              most common way a drop zone silently does nothing. */}
          <div
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              stage(event.dataTransfer.files)
            }}
            className={cn(
              'mt-2 rounded-[var(--radius-DEFAULT)] border border-dashed px-4 py-6',
              'text-center text-[12.5px] font-light transition-colors',
              dragging
                ? 'border-brand text-text-heading bg-brand/5'
                : 'border-line text-text-muted',
            )}
          >
            {queue.pendingCount > 0
              ? t('cases.files.uploading')
              : t('cases.files.dropHint')}
          </div>
        </div>

        {isLoading ? null : rows.length === 0 ? (
          <EmptyState
            icon={Paperclip}
            title={t('cases.files.emptyTitle')}
            description={t('cases.files.emptyDescription')}
          />
        ) : (
          groups.map((group) => (
            <section key={group.kind}>
              {/* The group heading carries its own count, so "how many CMRs
                  are on this shipment" is answered without counting rows. */}
              <h3 className="border-line-hair bg-surface-sunken flex items-center justify-between gap-3 border-y px-[18px] py-1.5">
                <span className="asa-eyebrow">{t(fileKindLabelKey(group.kind))}</span>
                <span className="text-text-muted asa-num text-[12px]">
                  {group.files.length}
                </span>
              </h3>

              <ul className="flex flex-col">
                {group.files.map((file, index) => (
                  <li
                    key={file.id}
                    className={cn(
                      'flex items-center gap-3 px-[18px] py-2.5',
                      index !== group.files.length - 1 && 'border-line-hair border-b',
                    )}
                  >
                    <FileText
                      size={15}
                      className="text-text-muted shrink-0"
                      aria-hidden
                    />

                    {/* The row is a button rather than a link: the href does not
                        exist until it is asked for, because a download URL lives
                        five minutes (AD-18). Rendering an <a> would mean either
                        minting a URL per row on every render or shipping a dead
                        link. */}
                    <button
                      type="button"
                      onClick={() => download.mutate(file.id)}
                      disabled={file.pending}
                      className={cn(
                        'flex min-w-0 flex-1 flex-col text-left',
                        'hover:[&>span:first-child]:text-brand',
                        'focus-visible:outline-brand focus-visible:outline-2',
                        'focus-visible:outline-offset-2 disabled:cursor-default',
                      )}
                    >
                      <span className="text-text-heading truncate text-[13px]">
                        {file.name}
                      </span>
                      {/* Uploader and FORMAT, which the AC names explicitly,
                          beside the size and the time. The document kind is
                          the heading above rather than a repetition here. */}
                      <span className="text-text-muted text-[12px] font-light">
                        {fileFormat(file.kind)} · {file.uploadedByName} ·{' '}
                        {format.timestamp(file.uploadedAt)} ·{' '}
                        {formatBytes(file.sizeBytes)}
                      </span>
                    </button>

                    {file.pending ? (
                      <StatusPill tone="attention">
                        {t('cases.files.pending')}
                      </StatusPill>
                    ) : (
                      /* Delete, in the row, in TWO PRESSES. The same shape the
                         cost-line table uses, and for the stronger reason: this
                         one destroys bytes in a bucket, and there is no undo
                         anywhere behind it. A confirmation dialog was the
                         alternative and is worse here — it takes over the
                         screen to ask about a row it then hides. */
                      <span className="flex shrink-0 items-center gap-0.5">
                        {armedId === file.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                remove.mutate(file.id)
                                setArmedId(null)
                              }}
                              disabled={remove.isPending}
                              aria-label={t('cases.files.deleteConfirmAction')}
                              title={t('cases.files.deleteConfirm', {
                                name: file.name,
                              })}
                              className="text-negative bg-negative-soft cursor-pointer rounded-[5px] px-1.5 py-0.5 text-[12px] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {t('cases.files.deleteConfirmAction')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setArmedId(null)}
                              aria-label={t('common.actions.cancel')}
                              title={t('common.actions.cancel')}
                              className="text-text-muted hover:bg-surface-hover cursor-pointer rounded-[5px] p-1"
                            >
                              <X size={14} aria-hidden />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setArmedId(file.id)}
                            aria-label={t('cases.files.delete', {
                              name: file.name,
                            })}
                            className="text-text-muted hover:text-negative hover:bg-surface-hover cursor-pointer rounded-[5px] p-1"
                          >
                            <Trash2 size={14} aria-hidden />
                          </button>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </Card>

      {/* What kind of paper this is, asked ONCE for the whole selection.
          Per-file would be the more precise question and the wrong one: eight
          photos of one reload are eight of the same kind, and asking eight
          times is how somebody starts answering without reading. A file that
          needs a different kind is uploaded on its own — the same act, once
          more. */}
      <FormSheet
        open={staged !== null}
        onClose={() => setStaged(null)}
        title={t('cases.files.kindTitle')}
        submitLabel={t('cases.files.kindSubmit', { count: staged?.length ?? 0 })}
        onSubmit={send}
      >
        <div className="flex flex-col gap-4">
          <p className="text-text-muted text-[13px] leading-snug font-light">
            {t('cases.files.kindHint')}
          </p>

          <Field label={t('cases.files.kind')} hint={t('cases.files.kindFieldHint')}>
            {(props) => (
              <Select
                {...props}
                value={stagedKind}
                onChange={(next) => setStagedKind(asFileKind(next))}
                options={FILE_KINDS.map((kind) => ({
                  value: kind,
                  label: t(fileKindLabelKey(kind)),
                }))}
              />
            )}
          </Field>

          {/* What is about to be sent, named. A sheet that said only "3 files"
              would be one somebody confirms without knowing they picked the
              wrong folder — and the queue makes that expensive to undo, because
              by then the rows exist. */}
          <ul className="border-line bg-surface-sunken flex max-h-40 flex-col gap-1 overflow-y-auto rounded-lg border px-3.5 py-2.5">
            {(staged ?? []).map((file) => (
              <li
                key={`${file.name}-${file.size}-${file.lastModified}`}
                className="text-text-heading flex items-center justify-between gap-3 text-[12.5px]"
              >
                <span className="min-w-0 truncate font-light">{file.name}</span>
                <span className="text-text-muted asa-num shrink-0 text-[12px]">
                  {formatBytes(file.size)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </FormSheet>
    </div>
  )
}
